import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Ruler, Weight, Grid3X3, Package, MapPin, Phone, Mail, Globe, LogIn, Award, Info, Truck, DollarSign, Building2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SolarPanel } from '../../types';
import { isAuthenticated } from '../../utils/auth';

interface FilterConfig {
  value: string;
  tolerance: string;
  toleranceType: 'percent' | 'units';
  operator?: 'equals' | 'gt' | 'lt' | 'gte' | 'lte';
}

interface SearchResultsProps {
  results: SolarPanel[];
  loading: boolean;
  searchState: {
    generalSearch: string;
    filters: Record<string, FilterConfig>;
  };
}

export const SearchResults: React.FC<SearchResultsProps> = ({ results, loading, searchState }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleSignInClick = () => {
    // Store current search state
    const stateToSave = {
      generalSearch: searchState.generalSearch,
      filters: searchState.filters,
      results,
      searched: true
    };
    sessionStorage.setItem('searchState', JSON.stringify(stateToSave));
    sessionStorage.setItem('returnPath', location.pathname);
    navigate('/user/login');
  };
  
  const handleSignUpClick = () => {
    // Store current search state
    const stateToSave = {
      generalSearch: searchState.generalSearch,
      filters: searchState.filters,
      results,
      searched: true
    };
    sessionStorage.setItem('searchState', JSON.stringify(stateToSave));
    sessionStorage.setItem('returnPath', location.pathname);
    navigate('/user/signup');
  };
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-pulse space-y-4 w-full">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">
          Search Results ({results.length})
        </h2>
        {results.length === 0 && (
          <p className="text-gray-600 mt-2">
            No panels found matching your criteria. Try adjusting your filters.
          </p>
        )}
      </div>

      <AnimatePresence>
        <div className="space-y-6">
          {results.map((panel, index) => (
            <motion.div
              key={panel.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              {/* Panel Header */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-full p-2">
                        <Zap className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{panel.SKU}</h3>
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {panel.Watts_W}W
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{panel.Description}</p>
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-4 flex items-center space-x-2">
                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                      {panel.ModuleEfficiency_percent}% Efficiency
                    </div>
                  </div>
                </div>
              </div>

              {/* Panel Content */}
              <div className="p-6">

                {/* Key Specifications Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                    <Zap className="h-6 w-6 text-green-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-green-700 uppercase tracking-wider">Power Output</p>
                    <p className="text-lg font-bold text-green-900">{panel.Watts_W}W</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                    <Grid3X3 className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">Efficiency</p>
                    <p className="text-lg font-bold text-blue-900">{panel.ModuleEfficiency_percent}%</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
                    <Ruler className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-purple-700 uppercase tracking-wider">Dimensions</p>
                    <p className="text-lg font-bold text-purple-900">{panel.Length_mm}×{panel.Width_mm}mm</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
                    <Weight className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-orange-700 uppercase tracking-wider">Weight</p>
                    <p className="text-lg font-bold text-orange-900">{panel.Weight_kg}kg</p>
                  </div>
                </div>

                {/* Electrical Specifications */}
                <div className="bg-gray-50 rounded-lg p-5 mb-6">
                  <div className="flex items-center mb-3">
                    <Award className="h-5 w-5 text-gray-700 mr-2" />
                    <h4 className="font-semibold text-gray-900">Electrical Specifications</h4>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Vmp</p>
                      <p className="text-lg font-bold text-gray-900">{panel.Vmp_V}V</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Imp</p>
                      <p className="text-lg font-bold text-gray-900">{panel.Imp_A}A</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Voc</p>
                      <p className="text-lg font-bold text-gray-900">{panel.Voc_V}V</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Isc</p>
                      <p className="text-lg font-bold text-gray-900">{panel.Isc_A}A</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Cell Type: <span className="font-semibold">{panel.CellType}</span></span>
                    <span className="text-gray-600">Cells: <span className="font-semibold">{panel.NumberOfCells_count}</span></span>
                  </div>
                </div>

                {/* Additional Notes */}
                {panel.remarks && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                    <div className="flex items-center mb-2">
                      <Info className="h-4 w-4 text-blue-600 mr-2" />
                      <h4 className="font-semibold text-blue-900">Additional Notes</h4>
                    </div>
                    <p className="text-sm text-blue-800">{panel.remarks}</p>
                  </div>
                )}

                {/* Inventory Section */}
                {panel.inventory && panel.inventory.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-100 rounded-full p-2 mr-3">
                        <Package className="h-5 w-5 text-green-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Available from {panel.inventory.length} Supplier{panel.inventory.length > 1 ? 's' : ''}</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {panel.inventory.map((inv: any) => (
                        <div key={inv.id} className="bg-gradient-to-br from-white to-gray-50 rounded-lg border-2 border-gray-200 hover:border-green-300 p-5 transition-all duration-300 hover:shadow-lg">
                          {/* Supplier Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                                <h5 className="font-bold text-gray-900 text-lg">{inv.supplier?.companyName}</h5>
                              </div>
                              <div className="flex items-center text-sm text-gray-600">
                                <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                <span>{inv.supplier?.city}, {inv.supplier?.state}, {inv.supplier?.country}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Inventory Details */}
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                              <Truck className="h-5 w-5 text-green-600 mx-auto mb-1" />
                              <p className="text-xs font-medium text-green-700 uppercase tracking-wider">Available Qty</p>
                              <p className="text-xl font-bold text-green-900">{inv.quantity}</p>
                            </div>
                            {inv.pricePerUnit && (
                              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                                <DollarSign className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                                <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">Price/Unit</p>
                                <p className="text-xl font-bold text-blue-900">${inv.pricePerUnit}</p>
                              </div>
                            )}
                          </div>

                          {/* Contact Information Section */}
                          {isAuthenticated() ? (
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <h6 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                                <Mail className="h-4 w-4 mr-2" />
                                Contact Information
                              </h6>
                              <div className="space-y-2">
                                {inv.supplier?.email && (
                                  <div className="flex items-center space-x-2">
                                    <Mail className="h-4 w-4 text-green-600" />
                                    <a 
                                      href={`mailto:${inv.supplier.email}`}
                                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm"
                                    >
                                      {inv.supplier.email}
                                    </a>
                                  </div>
                                )}
                                {inv.supplier?.phoneNumber && (
                                  <div className="flex items-center space-x-2">
                                    <Phone className="h-4 w-4 text-green-600" />
                                    <a 
                                      href={`tel:${inv.supplier.phoneNumber}`}
                                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm"
                                    >
                                      {inv.supplier.phoneNumber}
                                    </a>
                                  </div>
                                )}
                                {inv.supplier?.website && (
                                  <div className="flex items-center space-x-2">
                                    <Globe className="h-4 w-4 text-green-600" />
                                    <a 
                                      href={inv.supplier.website.startsWith('http') ? inv.supplier.website : `https://${inv.supplier.website}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm"
                                    >
                                      Visit Website
                                    </a>
                                  </div>
                                )}
                                {inv.supplier?.address && (
                                  <div className="text-sm text-gray-600 pt-2 border-t border-gray-300">
                                    <strong>Address:</strong> {inv.supplier.address}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            inv.supplier?.requiresLogin && (
                              <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="bg-blue-100 rounded-full p-2">
                                      <LogIn className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="font-semibold text-blue-900">Sign in to view supplier contact</p>
                                      <p className="text-sm text-blue-700">{inv.supplier.message}</p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={handleSignInClick}
                                      className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                    >
                                      Sign In
                                    </button>
                                    <button
                                      onClick={handleSignUpClick}
                                      className="px-4 py-2 bg-white text-blue-600 text-sm font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors"
                                    >
                                      Sign Up Free
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </AnimatePresence>
    </motion.div>
  );
};